import { createSlice, nanoid } from '@reduxjs/toolkit';

// Fare calculation constants
export const FARE_COMPONENTS = {
  BASE_FARE: 'base_fare',
  DISTANCE_RATE: 'distance_rate',
  TIME_RATE: 'time_rate',
  SURGE_MULTIPLIER: 'surge_multiplier',
  BOOKING_FEE: 'booking_fee',
  SERVICE_FEE: 'service_fee',
  TOLLS: 'tolls',
  TAXES: 'taxes'
};

// Surge pricing levels
export const SURGE_LEVELS = {
  NORMAL: { multiplier: 1.0, label: 'Normal' },
  LOW: { multiplier: 1.2, label: 'Low Demand' },
  MEDIUM: { multiplier: 1.5, label: 'Medium Demand' },
  HIGH: { multiplier: 2.0, label: 'High Demand' },
  PEAK: { multiplier: 2.5, label: 'Peak Hours' },
  EXTREME: { multiplier: 3.0, label: 'Extreme Demand' }
};

// Generate realistic fare configuration data
const generateFareConfiguration = () => {
  return {
    rateCards: {
      standard: {
        id: nanoid(),
        name: 'Standard Ride',
        baseFare: 3.50,
        distanceRate: 1.25, // per km
        timeRate: 0.35, // per minute
        minimumFare: 5.00,
        maximumFare: 100.00,
        bookingFee: 1.00,
        serviceFeePercentage: 5.0,
        cancellationFee: 3.00,
        waitingTimeRate: 0.25, // per minute after 3 minutes
        freeWaitingTime: 3, // minutes
        active: true,
        validFrom: '2024-01-01T00:00:00.000Z',
        validUntil: '2024-12-31T23:59:59.000Z'
      },
      premium: {
        id: nanoid(),
        name: 'Premium Ride',
        baseFare: 5.00,
        distanceRate: 1.75,
        timeRate: 0.50,
        minimumFare: 8.00,
        maximumFare: 150.00,
        bookingFee: 1.50,
        serviceFeePercentage: 5.0,
        cancellationFee: 5.00,
        waitingTimeRate: 0.40,
        freeWaitingTime: 5,
        active: true,
        validFrom: '2024-01-01T00:00:00.000Z',
        validUntil: '2024-12-31T23:59:59.000Z'
      },
      shared: {
        id: nanoid(),
        name: 'Shared Ride',
        baseFare: 2.50,
        distanceRate: 0.85,
        timeRate: 0.25,
        minimumFare: 3.50,
        maximumFare: 50.00,
        bookingFee: 0.75,
        serviceFeePercentage: 5.0,
        cancellationFee: 2.00,
        waitingTimeRate: 0.15,
        freeWaitingTime: 2,
        active: true,
        validFrom: '2024-01-01T00:00:00.000Z',
        validUntil: '2024-12-31T23:59:59.000Z'
      }
    },
    surgeZones: [
      {
        id: nanoid(),
        name: 'Downtown Business District',
        coordinates: [
          { lat: 40.7505, lng: -73.9934 },
          { lat: 40.7589, lng: -73.9851 },
          { lat: 40.7614, lng: -73.9776 },
          { lat: 40.7530, lng: -73.9859 }
        ],
        currentSurge: SURGE_LEVELS.MEDIUM,
        peakHours: [
          { start: '07:00', end: '09:30', surge: SURGE_LEVELS.HIGH },
          { start: '17:00', end: '19:30', surge: SURGE_LEVELS.HIGH }
        ],
        active: true
      },
      {
        id: nanoid(),
        name: 'Airport Terminal',
        coordinates: [
          { lat: 40.6892, lng: -74.1745 },
          { lat: 40.6950, lng: -74.1680 },
          { lat: 40.6920, lng: -74.1620 },
          { lat: 40.6862, lng: -74.1685 }
        ],
        currentSurge: SURGE_LEVELS.LOW,
        peakHours: [
          { start: '06:00', end: '08:00', surge: SURGE_LEVELS.MEDIUM },
          { start: '18:00', end: '22:00', surge: SURGE_LEVELS.MEDIUM }
        ],
        active: true
      }
    ],
    specialRates: {
      nightSurcharge: {
        active: true,
        startTime: '22:00',
        endTime: '06:00',
        surchargePercentage: 15.0
      },
      weekendSurcharge: {
        active: false,
        days: [0, 6], // Sunday, Saturday
        surchargePercentage: 10.0
      },
      holidaySurcharge: {
        active: true,
        dates: ['2024-12-25', '2024-12-31', '2025-01-01'],
        surchargePercentage: 25.0
      }
    },
    taxes: {
      salesTax: {
        rate: 8.25,
        applicable: true
      },
      cityTax: {
        rate: 2.5,
        applicable: true
      }
    }
  };
};

// Generate fare calculation history
const generateFareHistory = () => {
  const history = [];

  for (let i = 0; i < 100; i++) {
    const date = new Date();
    date.setDate(date.getDate() - Math.floor(Math.random() * 30));

    const rideTypes = ['standard', 'premium', 'shared'];
    const rideType = rideTypes[Math.floor(Math.random() * rideTypes.length)];

    const distance = (Math.random() * 20 + 2).toFixed(1);
    const duration = Math.floor(Math.random() * 45 + 10);
    const surge = Object.values(SURGE_LEVELS)[Math.floor(Math.random() * Object.values(SURGE_LEVELS).length)];

    history.push({
      id: nanoid(),
      tripId: nanoid(),
      rideType,
      distance: parseFloat(distance),
      duration,
      surge: surge.multiplier,
      baseFare: rideType === 'premium' ? 5.00 : rideType === 'shared' ? 2.50 : 3.50,
      distanceFare: parseFloat((distance * (rideType === 'premium' ? 1.75 : rideType === 'shared' ? 0.85 : 1.25)).toFixed(2)),
      timeFare: parseFloat((duration * (rideType === 'premium' ? 0.50 : rideType === 'shared' ? 0.25 : 0.35)).toFixed(2)),
      bookingFee: rideType === 'premium' ? 1.50 : rideType === 'shared' ? 0.75 : 1.00,
      serviceFee: 0,
      taxes: 0,
      totalFare: 0,
      calculatedAt: date.toISOString()
    });
  }

  // Calculate derived values
  history.forEach(record => {
    const subtotal = record.baseFare + record.distanceFare + record.timeFare + record.bookingFee;
    const surgedAmount = subtotal * record.surge;
    record.serviceFee = parseFloat((surgedAmount * 0.05).toFixed(2));
    record.taxes = parseFloat((surgedAmount * 0.1075).toFixed(2)); // 8.25% + 2.5%
    record.totalFare = parseFloat((surgedAmount + record.serviceFee + record.taxes).toFixed(2));
  });

  return history.sort((a, b) => new Date(b.calculatedAt) - new Date(a.calculatedAt));
};

const initialState = {
  configuration: generateFareConfiguration(),
  calculationHistory: generateFareHistory(),
  currentEstimate: null,
  surgeStatus: {
    lastUpdated: new Date().toISOString(),
    globalSurge: SURGE_LEVELS.NORMAL
  }
};

const fareSlice = createSlice({
  name: 'fares',
  initialState,
  reducers: {
    updateRateCard(state, action) {
      const { rideType, updates } = action.payload;
      if (state.configuration.rateCards[rideType]) {
        state.configuration.rateCards[rideType] = {
          ...state.configuration.rateCards[rideType],
          ...updates
        };
      }
    },

    addRateCard(state, action) {
      const { rideType, rateCard } = action.payload;
      state.configuration.rateCards[rideType] = {
        id: nanoid(),
        ...rateCard,
        validFrom: new Date().toISOString()
      };
    },

    updateSurgeZone(state, action) {
      const { zoneId, updates } = action.payload;
      const zoneIndex = state.configuration.surgeZones.findIndex(z => z.id === zoneId);
      if (zoneIndex !== -1) {
        state.configuration.surgeZones[zoneIndex] = {
          ...state.configuration.surgeZones[zoneIndex],
          ...updates
        };
      }
    },

    addSurgeZone(state, action) {
      state.configuration.surgeZones.push({
        id: nanoid(),
        ...action.payload,
        active: true
      });
    },

    updateGlobalSurge(state, action) {
      const { surgeLevel } = action.payload;
      state.surgeStatus.globalSurge = surgeLevel;
      state.surgeStatus.lastUpdated = new Date().toISOString();
    },

    calculateFareEstimate(state, action) {
      const { rideType, distance, estimatedDuration, pickupLocation, currentTime } = action.payload;
      const rateCard = state.configuration.rateCards[rideType];

      if (!rateCard || !rateCard.active) {
        state.currentEstimate = { error: 'Invalid ride type or inactive rate card' };
        return;
      }

      // Base calculation
      let baseFare = rateCard.baseFare;
      let distanceFare = distance * rateCard.distanceRate;
      let timeFare = estimatedDuration * rateCard.timeRate;
      let bookingFee = rateCard.bookingFee;

      let subtotal = baseFare + distanceFare + timeFare + bookingFee;

      // Apply minimum fare
      if (subtotal < rateCard.minimumFare) {
        subtotal = rateCard.minimumFare;
      }

      // Determine surge multiplier
      let surgeMultiplier = state.surgeStatus.globalSurge.multiplier;

      // Check for zone-specific surge
      const applicableZone = state.configuration.surgeZones.find(zone => {
        // Simplified point-in-polygon check (would need proper implementation)
        return zone.active && zone.currentSurge;
      });

      if (applicableZone) {
        surgeMultiplier = Math.max(surgeMultiplier, applicableZone.currentSurge.multiplier);
      }

      // Check for time-based surcharges
      const currentHour = new Date(currentTime || Date.now()).getHours();
      const currentDay = new Date(currentTime || Date.now()).getDay();

      // Night surcharge
      if (state.configuration.specialRates.nightSurcharge.active) {
        const nightStart = parseInt(state.configuration.specialRates.nightSurcharge.startTime.split(':')[0]);
        const nightEnd = parseInt(state.configuration.specialRates.nightSurcharge.endTime.split(':')[0]);

        if (currentHour >= nightStart || currentHour < nightEnd) {
          const nightSurcharge = 1 + (state.configuration.specialRates.nightSurcharge.surchargePercentage / 100);
          surgeMultiplier = Math.max(surgeMultiplier, nightSurcharge);
        }
      }

      // Weekend surcharge
      if (state.configuration.specialRates.weekendSurcharge.active &&
          state.configuration.specialRates.weekendSurcharge.days.includes(currentDay)) {
        const weekendSurcharge = 1 + (state.configuration.specialRates.weekendSurcharge.surchargePercentage / 100);
        surgeMultiplier = Math.max(surgeMultiplier, weekendSurcharge);
      }

      // Apply surge
      const surgedAmount = subtotal * surgeMultiplier;

      // Calculate fees and taxes
      const serviceFee = surgedAmount * (rateCard.serviceFeePercentage / 100);
      const salesTax = surgedAmount * (state.configuration.taxes.salesTax.rate / 100);
      const cityTax = surgedAmount * (state.configuration.taxes.cityTax.rate / 100);
      const totalTaxes = salesTax + cityTax;

      const totalFare = surgedAmount + serviceFee + totalTaxes;

      // Apply maximum fare limit
      const finalFare = Math.min(totalFare, rateCard.maximumFare);

      state.currentEstimate = {
        rideType,
        breakdown: {
          baseFare: parseFloat(baseFare.toFixed(2)),
          distanceFare: parseFloat(distanceFare.toFixed(2)),
          timeFare: parseFloat(timeFare.toFixed(2)),
          bookingFee: parseFloat(bookingFee.toFixed(2)),
          subtotal: parseFloat(subtotal.toFixed(2)),
          surgeMultiplier: parseFloat(surgeMultiplier.toFixed(1)),
          surgedAmount: parseFloat(surgedAmount.toFixed(2)),
          serviceFee: parseFloat(serviceFee.toFixed(2)),
          taxes: parseFloat(totalTaxes.toFixed(2)),
          totalFare: parseFloat(finalFare.toFixed(2))
        },
        estimatedRange: {
          min: parseFloat((finalFare * 0.9).toFixed(2)),
          max: parseFloat((finalFare * 1.1).toFixed(2))
        },
        calculatedAt: new Date().toISOString()
      };
    },

    addFareCalculation(state, action) {
      state.calculationHistory.unshift({
        id: nanoid(),
        ...action.payload,
        calculatedAt: new Date().toISOString()
      });

      // Keep only last 1000 calculations
      if (state.calculationHistory.length > 1000) {
        state.calculationHistory = state.calculationHistory.slice(0, 1000);
      }
    },

    updateSpecialRates(state, action) {
      const { rateType, updates } = action.payload;
      if (state.configuration.specialRates[rateType]) {
        state.configuration.specialRates[rateType] = {
          ...state.configuration.specialRates[rateType],
          ...updates
        };
      }
    },

    updateTaxRates(state, action) {
      const { taxType, rate, applicable } = action.payload;
      if (state.configuration.taxes[taxType]) {
        state.configuration.taxes[taxType] = {
          ...state.configuration.taxes[taxType],
          rate: rate !== undefined ? rate : state.configuration.taxes[taxType].rate,
          applicable: applicable !== undefined ? applicable : state.configuration.taxes[taxType].applicable
        };
      }
    }
  }
});

export const {
  updateRateCard,
  addRateCard,
  updateSurgeZone,
  addSurgeZone,
  updateGlobalSurge,
  calculateFareEstimate,
  addFareCalculation,
  updateSpecialRates,
  updateTaxRates
} = fareSlice.actions;

export default fareSlice.reducer;